import { useState } from 'react';
import Reveal from './Reveal';
import { useApp } from '../store';
import { can } from '../permissions';
import { assistantName, setupProgress } from '../assistant';
import { relativeTime } from '../utils';
import { currentEnv } from '../env';
import { trackerInstructions as buildTrackerInstructions } from '../tracker-format';
import type { AssistantEntry } from '../types';
import type { ProjectRole } from '../permissions';

/**
 * ONE ASSISTANT, OPENED — everything about it, on a page of its own.
 *
 * WHY A PAGE AND NOT THE WIZARD IT REPLACES. The wizard destroyed itself: its
 * four steps rendered only while no assistant existed, so pressing "Add
 * assistant" at step one unmounted steps two, three and four in the same
 * instant. Vinnie found it the first time he used it — "I have added it, and no
 * other settings was implemented". They were implemented. They were unreachable.
 *
 * A wizard was the wrong shape for this even without the bug, because setting an
 * assistant up is not a thing you do once. Six weeks later somebody swaps who
 * pushes, or reissues a password, or renames it — and walking four steps to
 * change one of them is the interface arguing with the person using it. A page
 * is editable at any moment, in any order, forever.
 *
 * NOTHING HERE IS OPTIONAL-BY-SILENCE. Every step says what it is for, what
 * happens when you choose it, and how you will know it worked. That was asked
 * for in as many words on 17 August: "every step must have something that
 * assists the user in completing the process and finding success". So each one
 * carries its own help, and the strip at the top always names the single next
 * thing to do rather than leaving somebody to infer it from what looks blank.
 */
export default function AssistantPage({ id, onBack }: { id: string; onBack: () => void }) {
  const meta = useApp((s) => s.meta);
  const projectId = useApp((s) => s.projectId);
  const role = useApp((s) => s.shownRole);
  const setMeta = useApp((s) => s.setMeta);
  const removeFromProject = useApp((s) => s.removeFromProject);
  const canSettings = can(role, 'editSettings');

  const entry: AssistantEntry = meta.assistants?.[id] ?? {};
  const name = assistantName(entry, id);
  const progress = setupProgress(entry);
  /*
   * HOW MANY STEPS THERE ARE, worked out once.
   *
   * Written out at each step instead, it went wrong the moment step 5 was added:
   * four of the five headings were corrected and the account step went on saying
   * "step 3 of 4" underneath a page that had five. Saying it once means the
   * numbering cannot disagree with itself again.
   *
   * An assistant that pushes has an account to be given and a tool to be handed;
   * one that does not has neither, so it is five or three and never four.
   */
  const givesAccount = entry.pusher === 'assistant';
  const steps = givesAccount ? 5 : 3;

  const [renaming, setRenaming] = useState(name);
  const [removing, setRemoving] = useState(false);
  const [copied, setCopied] = useState('');
  /**
   * The password, shown once. It lives on the PAGE and not in the form that
   * produced it, because writing the account's address unmounts that form —
   * see the note beside the account step.
   */
  const [handed, setHanded] = useState('');
  const [handedTo, setHandedTo] = useState('');

  /**
   * THE COMMAND THAT PROVES THIS WORKED, naming the board and the environment.
   *
   * Two things about it were wrong for as long as the board has been on
   * Postgres, and neither announced itself. It said `--project <id>`, which the
   * check no longer takes — a board is named by its KEY now, and an unrecognised
   * flag is ignored rather than refused, so it quietly checked whichever board
   * came first. And on the live board it emitted no environment flag at all,
   * which was correct, while the tool it named could only ever look at the test
   * copy. So the one command in FlightDeck whose entire job is to tell you
   * whether the setup took effect was answering about a different board, in a
   * database nothing had read since 13 August.
   */
  /*
   * REWRITTEN 18 AUGUST. This named `npm run check-answering`, which is a script
   * in FlightDeck's OWN package.json — so it worked for exactly one person, the
   * one building the product. See the note on `pushCommand` below; the fix is
   * the same and the reason is the same.
   */
  const checkCommand = `node flightdeck.mjs notes`;

  /**
   * ── THE COMMAND THAT PUTS THE FILE ON THE BOARD ─────────────────────────
   *
   * WHY THIS EXISTS. Step 4 handed over the document format and called itself
   * "the last thing you have to do", which was not true for anybody who said
   * the assistant does the pushing: it now knew how to WRITE the file and
   * nothing about how to get it onto a board — which board, or which of the two
   * copies of FlightDeck. Vinnie asked the question that follows from that on
   * 17 August: "I just created an assistant in UAT and posted the instructions
   * into the CLI, how do I know it is not using the Live TRACKER.md?"
   *
   * The instructions themselves carry no environment at all, so they were never
   * the risk. The risk was the missing command, filled in from memory by
   * whoever was there — which is exactly how a `--live` ends up somewhere it
   * was not meant to be.
   *
   * SO IT IS BUILT HERE, from what this page already knows: which copy it is
   * (currentEnv) and which board is open (meta.key). The test copy emits a test
   * command; the live copy emits a live one. Nothing is left to be remembered.
   *
   * `--live` IS EMITTED RATHER THAN ASSUMED. push-tracker defaults to the test
   * copy, so the live page has to say so out loud. Naming it also means the
   * word is visible on screen before it is ever run, which is the only moment
   * anybody would notice it being wrong.
   *
   * NO `--write`. The command handed over is the one that changes nothing. What
   * it prints is the list of cards that WOULD change, and the sentence under it
   * says so in those words — "preview" was mine, and meant nothing to anybody.
   */
  /*
   * IT HANDED OUT A COMMAND ONLY THIS MACHINE COULD RUN, until 18 August.
   *
   * `npx tsx scripts/push-tracker.ts …` names a file in FLIGHTDECK'S OWN
   * REPOSITORY. There is no FlightDeck folder on a customer's computer and
   * there never will be, so TRINRE's assistant would have got "no such file"
   * and no explanation — while the screen looked finished, because the only
   * person who ever ran it was the one standing in the folder it needed.
   *
   * Vinnie, 18 August: "All instructions on the app need to happen as if they
   * are not on my machine. Which they are not."
   *
   * SO THE APP HANDS OVER A TOOL, one file, downloaded from this page and run
   * with nothing installed. And the tool knows which copy of FlightDeck it came
   * from, because the address is stamped into it when it is built — which is
   * why there is no longer a --live to emit or to get wrong. That question of
   * his from the day before — "how do I know it is not using the Live
   * TRACKER.md" — cannot now be asked: the file downloaded from here can only
   * reach here.
   *
   * NO `--write`. What is handed over is the command that changes nothing and
   * prints the cards that WOULD change.
   */
  const pushCommand =
    `node flightdeck.mjs push --file TRACKER.md` +
    (meta.key ? ` --board ${meta.key}` : ' --board YOUR-BOARD-KEY');
  const signInCommand = `node flightdeck.mjs sign-in`;

  /** Write one field back, leaving the rest of the entry exactly as it was. */
  const change = (patch: Partial<AssistantEntry>) =>
    setMeta({
      assistants: { ...(meta.assistants ?? {}), [id]: { ...entry, ...patch } },
    });

  const copy = (text: string, what: string) => {
    void navigator.clipboard?.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(''), 2000);
  };

  /*
   * REMOVAL TAKES BOTH HALVES. An assistant is a register entry and, usually,
   * an account. Taking away only the entry stops it answering comments and
   * leaves it able to sign in and write cards — a revocation that revokes
   * nothing, done while showing somebody a tidy list.
   */
  const remove = async () => {
    const rest = { ...(meta.assistants ?? {}) };
    delete rest[id];
    setMeta({ assistants: rest });
    if (entry.email) await removeFromProject(projectId, entry.email);
    onBack();
  };

  /* THIS BOARD'S OWN TYPE NAMES — #302. Instructions listing four fixed
     words would tell an assistant to write something this board refuses. */
  const issueTypes = useApp((s) => s.issueTypes);
  const instructions = buildTrackerInstructions(meta.name, issueTypes);

  return (
    <section className="panel assistant-page">
      <button className="btn btn-small" onClick={onBack}>
        ← All assistants
      </button>

      <div className="asst-head">
        <h2>{name}</h2>
        {progress.finished ? (
          <span className="asst-tag asst-tag-on">working</span>
        ) : (
          <span className="asst-tag asst-tag-todo">setup unfinished</span>
        )}
      </div>
      <p className="hint">
        {entry.addedAt ? `Added ${relativeTime(entry.addedAt)}. ` : ''}
        {entry.lastSeen
          ? `Last worked on this board ${relativeTime(entry.lastSeen)}.`
          : 'It has not worked on this board yet.'}
      </p>

      {/*
        THE ONE NEXT THING, ALWAYS NAMED.
        A page of four sections leaves somebody scanning for whichever looks
        unfinished. This says it outright, and says it in the order the steps
        actually have to happen, so there is never a question of where to start.
      */}
      <div className={`asst-next ${progress.finished ? 'is-done' : ''}`}>
        {progress.finished ? (
          <>
            <strong>Nothing left to do.</strong> {name} has worked on this board, so it can find it
            and is permitted to write here. Everything below stays editable.
          </>
        ) : (
          <>
            <strong>Next:</strong> {progress.todo}
          </>
        )}
      </div>

      <Step
        n={1}
        of={steps}
        title="Name it"
        done={progress.done.includes('name')}
        why="This is what you will see in the list, on any card it is given, and beside any comment it answers. Only you see it — the assistant is never told its name."
      >
        <label className="stack">
          Its name
          <input
            className="input"
            value={renaming}
            disabled={!canSettings}
            placeholder="My assistant"
            onChange={(e) => setRenaming(e.target.value)}
          />
        </label>
        <div className="btn-row">
          <button
            className="btn btn-primary btn-small"
            disabled={!canSettings || !renaming.trim() || renaming.trim() === name}
            onClick={() => change({ name: renaming.trim() })}
          >
            {renaming.trim() === name ? 'Saved' : 'Save the name'}
          </button>
        </div>
      </Step>

      {/*
        STEP 2 DECIDES WHETHER STEP 3 EXISTS, so it is asked before anybody is
        sent to issue credentials. The screen used to answer it silently and
        wrongly — see the note on AssistantEntry.pusher.
      */}
      <Step
        n={2}
        of={steps}
        title="Say who puts the tracker on the board"
        done={progress.done.includes('pusher')}
        why="Your assistant writes a tracker file. Somebody then has to put that file onto this board — either you, or the assistant itself. The answer decides whether it needs an account, so nothing below makes sense until this is settled."
      >
        <div className="add-roles">
          <button
            type="button"
            className={`add-role${entry.pusher === 'person' ? ' on' : ''}`}
            aria-pressed={entry.pusher === 'person'}
            disabled={!canSettings}
            onClick={() => change({ pusher: 'person' })}
          >
            <span className="add-radio" />
            <span>
              <b>I will</b>
              <em>
                You run the push yourself while signed in. The assistant never touches this board,
                so there are no credentials to hand over or take back.
              </em>
            </span>
          </button>
          <button
            type="button"
            className={`add-role${entry.pusher === 'assistant' ? ' on' : ''}`}
            aria-pressed={entry.pusher === 'assistant'}
            disabled={!canSettings}
            onClick={() => change({ pusher: 'assistant' })}
          >
            <span className="add-radio" />
            <span>
              <b>{name} will</b>
              <em>
                It signs in and writes here itself, so it needs an account. This is the usual
                answer, and the one that lets it work without you.
              </em>
            </span>
          </button>
        </div>
        <p className="hint">
          Not sure? Choose <strong>I will</strong>. It is the smaller permission, it needs no
          account, and you can change this at any time — the account step reappears if you do.
        </p>
      </Step>

      {entry.pusher === 'assistant' && (
        <Step
          n={3}
          of={steps}
          title="Give it an account"
          done={progress.done.includes('account')}
          why="It gets an ordinary account and signs in like anybody else. Everything it writes is checked against the same rules as everybody else's, at the rank you give it. Assistants get no shortcut and no extra powers — that is deliberate, because a shortcut is exactly where an unnoticed extra permission would end up."
        >
          {/*
            THE PASSWORD IS HELD BY THIS PAGE, NOT BY THE FORM THAT PRODUCED IT.
            ORDER MATTERS HERE, and getting it wrong threw a password away.

            It used to be state inside AssistantAccount, which was rendered only
            while `entry.email` was empty. Creating the account writes the
            address — so the page re-rendered, the condition flipped, and the
            component holding the one password anybody would ever see was
            unmounted in the same instant it received it. Vinnie's test sheet on
            17 August: "No password form was presented." The account existed and
            its password was gone.

            The same shape as the wizard bug three hours earlier: a thing that
            destroys itself at the moment it succeeds. So `handed` is checked
            FIRST and lives on the page, which nothing here unmounts.
          */}
          {handed ? (
            <div className="card add-card handed">
              <p className="handed-label">Its password — {handedTo}</p>
              <p className="handed-value">{handed}</p>
              <div className="handed-actions">
                <button className="btn" onClick={() => copy(handed, 'password')}>
                  {copied === 'password' ? 'Copied' : 'Copy'}
                </button>
                <button className="btn" onClick={() => setHanded('')}>
                  I have saved it
                </button>
              </div>
              <p className="hint">
                This is the only time it is shown. Put it wherever {name} keeps its passwords before
                you leave this page — nothing here can read it back. If it does get lost, it is not
                the end of anything: give it a new one on People and access.
              </p>
            </div>
          ) : entry.email ? (
            <>
              <div className="asst-account">
                <div>
                  <b>{entry.email}</b>
                  <span className="hint"> — signs in with this, and may edit cards on this board.</span>
                </div>
              </div>
              <p className="hint">
                Lost the password? It cannot be read back — it was shown once and never stored. Give
                it a new one on <strong>People and access</strong>, where its account lives.
              </p>
            </>
          ) : (
            <AssistantAccount
              id={id}
              name={name}
              onIssued={(password, address) => {
                setHandedTo(address);
                setHanded(password);
              }}
            />
          )}
        </Step>
      )}

      {entry.pusher === 'person' && (
        <p className="hint asst-skipped">
          <strong>No account needed, then.</strong> You push the file yourself while signed in, so{' '}
          {name} never signs in here. Change your answer above and this step comes back.
        </p>
      )}

      {/*
        SPLIT IN TWO ON 18 AUGUST. Teaching an assistant the document format and
        giving it a tool to push with are two different jobs, and both were in
        one step — thirteen blocks at one visual level, which is most of why it
        read as a pile. Both still tick together, because the only EVIDENCE
        either happened is the board hearing from the assistant; see
        setupProgress, which counts instructions as done on `lastSeen` and not
        on a Copy press.
      */}
      <Step
        n={givesAccount ? 4 : 3}
        of={steps}
        title="Teach it the format"
        done={progress.done.includes('instructions')}
        why="Your assistant writes the tracker file. This is the shape the board reads, and it is the one thing that has to be pasted into the assistant itself."
      >
        <div className="cmd-row">
          <code className="cmd cmd-doc">{instructions}</code>
          <button className="btn btn-small" onClick={() => copy(instructions, 'instructions')}>
            {copied === 'instructions' ? 'Copied' : 'Copy'}
          </button>
        </div>
      </Step>

      {entry.pusher === 'assistant' && (
        <Step
          n={steps}
          of={steps}
          title="Give it the tool"
          done={progress.done.includes('instructions')}
          why="One file, into the folder your assistant works in. Nothing is installed."
        >
          <div className="asst-ledger">
            <div className="asst-row">
              <div className="asst-when">Once</div>
              <div>
                <p className="asst-what">Get the tool</p>
                <p className="asst-note">
                  One file, about 15&nbsp;KB. Put it in the folder your assistant works in — not
                  here, and not anywhere in particular. It already knows which FlightDeck it
                  belongs to.
                </p>
                <div className="cmd-row">
                  <a className="btn btn-small btn-primary" href="/flightdeck.mjs" download="flightdeck.mjs">
                    Download flightdeck.mjs
                  </a>
                  <span className="hint">a file, not a folder · needs Node 18 or newer</span>
                </div>
              </div>
            </div>

            <div className="asst-row">
              <div className="asst-when">Once</div>
              <div>
                <p className="asst-what">Sign it in</p>
                <p className="asst-note">
                  It asks for the address and password of the account above. Only a token is kept,
                  and it can be signed out — the password itself is never written down.
                </p>
                <div className="cmd-row">
                  <code className="cmd">{signInCommand}</code>
                  <button className="btn btn-small" onClick={() => copy(signInCommand, 'signin')}>
                    {copied === 'signin' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>

            <div className="asst-row">
              <div className="asst-when">Each time</div>
              <div>
                <p className="asst-what">Put the work on the board</p>
                <p className="asst-note">Run this whenever the work changes.</p>
                <div className="cmd-row">
                  <code className="cmd">{pushCommand}</code>
                  <button className="btn btn-small" onClick={() => copy(pushCommand, 'push')}>
                    {copied === 'push' ? 'Copied' : 'Copy'}
                  </button>
                </div>
                {/*
                  WHAT IT DOES AND DOES NOT DO, in the words somebody who has not
                  read the code would use — "preview" was mine and meant nothing
                  to anybody.
                */}
                <p className="asst-after">
                  <strong>The first run changes nothing.</strong> It prints a list of the cards it
                  would add or alter and then stops. Read that list: the cards should be ones you
                  recognise. If it wants to change work you did not touch, stop and do not go
                  further. When it looks right, run it again with <code>--write</code> on the end.
                </p>
              </div>
            </div>

            <div className="asst-row">
              <div className="asst-when">If in doubt</div>
              <div>
                <p className="asst-what">Check it can see the board</p>
                <p className="asst-note">
                  Reads back the notes left for this assistant. An answer — even an empty one —
                  proves it reached the board as the right account.
                </p>
                <div className="cmd-row">
                  <code className="cmd">{checkCommand}</code>
                  <button className="btn btn-small" onClick={() => copy(checkCommand, 'command')}>
                    {copied === 'command' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/*
            THE ONE LINE THAT IS ONLY TRUE HERE. Everything above is what a
            customer reads on their own FlightDeck, where there is no test copy
            and no live board — they have one FlightDeck, and telling them
            theirs "cannot reach the live board" says nothing.
            Vinnie, 18 August: "No wordings should be specific to my environment
            but to an environment a customer would use."
          */}
          {currentEnv() !== 'live' && (
            <p className="setup-trouble">
              This is the test copy. The file you download here reaches the test copy and nothing
              else — the live address is not in it.
            </p>
          )}

          {/*
            HOW YOU KNOW IT WORKED — asked for by name, and the step that was
            missing entirely. Everything above is something you do; this is the
            only one that tells you whether any of it took effect. Nearly lost
            on 18 August while the step was being rebuilt, which is why the
            check for it names the words rather than the section.
          */}
          <div className="asst-proof">
            <strong>How you will know it worked.</strong>{' '}
            {entry.lastSeen ? (
              <>
                It already has. {name} last wrote to this board {relativeTime(entry.lastSeen)}, and
                this line updates every time it does.
              </>
            ) : (
              <>
                Ask your assistant to put its tracker on the board. When it does, this page will say
                when it last worked here instead of &ldquo;has not worked on this board yet&rdquo;,
                and the badge at the top turns to <em>working</em>. Nothing else needs checking — if
                that line changes, it found the board, was allowed in, and wrote to it.
              </>
            )}
          </div>
        </Step>
      )}

      {/* ── What it may do here, once it is working ──────────────────────── */}
      <div className="asst-block">
        <h3>Let it answer comments</h3>
        <label className="asst-switch">
          <button
            className={`switch ${entry.mayAnswer === true ? 'on' : ''}`}
            role="switch"
            aria-checked={entry.mayAnswer === true}
            aria-label={`Let ${name} read and answer comments on this board`}
            disabled={!canSettings}
            onClick={() => change({ mayAnswer: entry.mayAnswer !== true })}
          >
            <span className="switch-knob" />
          </button>
          <span className="hint">
            {entry.mayAnswer === true
              ? 'On — it can read comments here and answer on the card.'
              : 'Off — comments are not read. Nothing is answered.'}
          </span>
        </label>
        <p className="hint">
          Turning this on does not connect anything. FlightDeck never contacts your assistant — your
          assistant checks this switch itself before it does anything, so turning it off actually
          stops it rather than just hiding a button. Anything it writes appears under{' '}
          {name}&apos;s own name, never under yours, so nobody has to wonder who wrote what.
        </p>
      </div>

      {/* ── Taking it off the board ──────────────────────────────────────── */}
      <div className="asst-block asst-danger">
        <h3>Remove {name}</h3>
        {removing ? (
          <>
            <p className="hint">
              It comes off this list and loses access to this board, so it can no longer read or
              write here.{' '}
              {entry.email
                ? `Its account (${entry.email}) is not deleted — do that on People and access if you want it gone entirely.`
                : 'It has no account recorded, so there are no credentials to take away.'}
            </p>
            <div className="btn-row">
              <button className="btn btn-small" onClick={() => void remove()}>
                Remove it
              </button>
              <button className="btn btn-small" onClick={() => setRemoving(false)}>
                Keep it
              </button>
            </div>
          </>
        ) : (
          <button className="btn btn-small" disabled={!canSettings} onClick={() => setRemoving(true)}>
            Remove this assistant
          </button>
        )}
      </div>

      <div className="setup-more">
       <Reveal summary="Checking it can see the board">
        <p className="hint">
          Run this in the folder you downloaded the tool into, once it is signed in. It reads back
          the notes left for this assistant — so an answer, even an empty one, proves it can reach
          the board as the right account.
        </p>
        <div className="cmd-row">
          <code className="cmd">{checkCommand}</code>
          <button className="btn btn-small" onClick={() => copy(checkCommand, 'command')}>
            {copied === 'command' ? 'Copied' : 'Copy'}
          </button>
        </div>
       </Reveal>
      </div>
    </section>
  );
}

/**
 * One numbered step, with its own help and its own tick.
 *
 * THE TICK IS THE POINT. Four sections with headings look identical whether
 * they are finished or untouched, so somebody returning to a half-set-up
 * assistant has to read all of them to work out where they stopped. The tick
 * comes from setupProgress, which is also what the strip at the top reads —
 * one answer, so the two can never disagree.
 */
function Step({
  n,
  of,
  title,
  why,
  done,
  children,
}: {
  n: number;
  of: number;
  title: string;
  why: string;
  done: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`asst-step${done ? ' is-done' : ''}`}>
      <div className="asst-step-head">
        <span className="asst-tick" aria-hidden="true">
          {done ? '✓' : n}
        </span>
        <h3>
          <span className="asst-step-of">
            Step {n} of {of}
          </span>
          {title}
          {done && <span className="asst-done-word"> — done</span>}
        </h3>
      </div>
      <p className="hint asst-why">{why}</p>
      {children}
    </div>
  );
}

/**
 * AN ACCOUNT FOR THE ASSISTANT, ISSUED FROM HERE.
 *
 * WHY HERE AND NOT ON PEOPLE AND ACCESS. Because this is where somebody is
 * already setting the assistant up, and sending them to the screen for
 * colleagues to add a coding tool as though it were one was the seam showing.
 *
 * IT IS THE SAME MACHINERY AS ADDING A PERSON, deliberately. The account is
 * made by the same function, holds a real rank, and is judged by the same
 * policies — there is no separate, weaker path for assistants, because a
 * separate path is where a quiet extra permission ends up living. The only
 * difference is `assistant: true`, which decides which screen lists it.
 *
 * MEMBER, WITH NO CHOICE OFFERED. Pushing a tracker and answering comments both
 * need the rank that edits cards; nothing an assistant does needs the rank above
 * it, and a dropdown here would be an invitation to hand one out.
 *
 * SHOWN ONCE, AND NOT BY THIS COMPONENT. The password is generated on
 * Supabase's machines and handed back in that one response — never written
 * down, never emailed, and not readable again. It is passed straight up to the
 * page through `onIssued` rather than held here, because creating the account
 * writes the address onto the entry, and the page stops rendering this form the
 * moment there is one. Holding it here meant it was destroyed on arrival.
 */
export function AssistantAccount({
  id,
  name,
  onIssued,
}: {
  id: string;
  name: string;
  /** Called with the one password, for whoever can still be on screen to show.
      Optional only so the form can be rendered on its own in the suite. */
  onIssued?: (password: string, address: string) => void;
}) {
  const role = useApp((s) => s.shownRole);
  const projectId = useApp((s) => s.projectId);
  const addPerson = useApp((s) => s.addPerson);
  const access = useApp((s) => s.access);
  const meta = useApp((s) => s.meta);
  const setMeta = useApp((s) => s.setMeta);
  const [email, setEmail] = useState('');
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState('');

  const mayAdd = can(role, 'setRoles');
  const address = email.trim().toLowerCase();
  const looksLikeAddress = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);

  /*
   * ALREADY A PERSON ON THIS BOARD — asked about, not done quietly.
   *
   * Two very different things arrive here with an address already on the guest
   * list. One is an assistant that predates this screen, added as a person
   * because that was the only way there was; converting it is exactly right.
   * The other is a mistyped address, and converting THAT takes a colleague off
   * People and access without anybody being told. Indistinguishable from here,
   * so the screen asks.
   */
  const wasAPerson = access.emails.includes(address);

  return (
    <div className="stack">
      <label className="stack">
        Its address
        <input
          className="input"
          value={email}
          disabled={!mayAdd || busy}
          placeholder={`${id}@your-company.com`}
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          onChange={(e) => {
            setEmail(e.target.value);
            setTrouble('');
          }}
        />
      </label>
      <p className="hint">
        Any address it can be identified by — it does not have to be a real inbox, and nothing is
        ever sent to it. An assistant has no inbox, so the password is handed to you here on screen
        instead of emailed. It will be able to edit cards on this board and nothing else — it
        cannot change settings, add or remove people, or see any of your other boards.
      </p>
      {wasAPerson && !sure && (
        <div className="card add-card">
          <p>
            <strong>{address}</strong> is already on People and access as a person.
          </p>
          <p className="hint">
            Continuing lists it as an assistant instead, so it will no longer appear there. That is
            right for an assistant added before this screen existed, and wrong for a colleague whose
            address was mistyped.
          </p>
          <button className="btn" onClick={() => setSure(true)}>
            It is an assistant — continue
          </button>
        </div>
      )}
      {trouble && <p className="field-error">{trouble}</p>}
      <div className="btn-row">
        <button
          className="btn btn-primary"
          disabled={!mayAdd || busy || !looksLikeAddress || (wasAPerson && !sure)}
          onClick={async () => {
            setBusy(true);
            setTrouble('');
            try {
              const password = await addPerson(address, 'member', { first: name, last: '' }, {
                title: 'Assistant',
                boards: [{ id: projectId, role: 'member' as ProjectRole }],
                assistant: true,
              });
              if (!password) {
                setTrouble('The account was made, but no password came back. Try again.');
                return;
              }
              /* The entry and the account, joined. Without the address on the
                 entry the two halves are held together only by somebody
                 remembering, and removal cannot take the credentials with it. */
              setMeta({
                assistants: {
                  ...(meta.assistants ?? {}),
                  [id]: {
                    ...(meta.assistants?.[id] ?? {}),
                    name: name || id,
                    addedAt: meta.assistants?.[id]?.addedAt ?? Date.now(),
                    email: address,
                  },
                },
              });
              /*
                HANDED UP, NOT HELD. The setMeta above writes the address, which
                is what stops this component being rendered at all — so anything
                kept in its own state from here on is gone before it can be
                read. The page holds the password instead.
              */
              onIssued?.(password, address);
            } catch (e) {
              setTrouble((e as { message?: string })?.message || 'That did not work.');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Creating…' : 'Create its account'}
        </button>
      </div>
    </div>
  );
}
